import React from "react"
import { ResponsivePie } from "@nivo/pie"
import {
  LEGEND_POSITION,
  STATISTIC_TYPE,
} from "types/ReportingDashboard/elements.types"
import { PieChartSlice, PieChartStatisticGroup } from "utils/convertDataToTable"
import { chartColorScheme } from "constants/chartColorScheme"
import { PieChartTooltip } from "./CustomTooltip/CustomTooltip.component"

/**
 * Props for PieChart component
 */
interface PieChartProps {
  /** Array of data objects containing statistic and data arrays */
  data: PieChartStatisticGroup[]
  /** Whether this is displayed as a chart */
  isChart?: boolean
  /** The currently selected statistic */
  selectedStatistic?: STATISTIC_TYPE
  /** Position for the legend */
  legendPosition?: LEGEND_POSITION
  /** Whether to hide the external arc link labels (arms/lines) */
  hideArcLabels?: boolean
}

/**
 * Interface for chart margins
 */
interface ChartMargins {
  top: number
  right: number
  bottom: number
  left: number
}

/**
 * Helper function to get legend configuration based on position
 */
const getLegendConfig = (position: LEGEND_POSITION) => {
  const baseLegendProps = {
    itemWidth: 100,
    itemHeight: 18,
    itemDirection: "left-to-right" as const,
    itemOpacity: 1,
    symbolSize: 12,
    symbolShape: "circle" as const,
    effects: [
      {
        on: "hover" as const,
        style: { itemTextColor: "#000" },
      },
    ],
    itemsSpacing: 1,
  }

  switch (position) {
    case LEGEND_POSITION.RIGHT:
      return [
        {
          ...baseLegendProps,
          anchor: "right" as const,
          direction: "column" as const,
          justify: false,
          translateX: 120,
          translateY: 0,
        },
      ]
    case LEGEND_POSITION.LEFT:
      return [
        {
          ...baseLegendProps,
          anchor: "left" as const,
          direction: "column" as const,
          justify: false,
          translateX: -120,
          translateY: 0,
        },
      ]
    case LEGEND_POSITION.BOTTOM:
      return [
        {
          ...baseLegendProps,
          anchor: "bottom" as const,
          direction: "row" as const,
          justify: false,
          translateX: 0,
          translateY: 50,
          itemsSpacing: 2,
          itemWidth: 80,
        },
      ]
    case LEGEND_POSITION.TOP:
      return [
        {
          ...baseLegendProps,
          anchor: "top" as const,
          direction: "row" as const,
          justify: false,
          translateX: 0,
          translateY: -40,
          itemsSpacing: 2,
          itemWidth: 80,
        },
      ]
    case LEGEND_POSITION.NONE:
      return []
    default:
      return [
        {
          ...baseLegendProps,
          anchor: "right" as const,
          direction: "column" as const,
          justify: false,
          translateX: 120,
          translateY: 0,
        },
      ]
  }
}

/**
 * Helper function to get margins based on legend position
 */
const getMargins = (position: LEGEND_POSITION): ChartMargins => {
  const increasedRightPadding = 200
  const defaultRightPadding = 40
  const defaultLeftPadding = 40
  const increasedLeftPadding = 140
  const defaultTopPadding = 40
  const defaultBottomPadding = 80
  const increasedVerticalPadding = 60

  switch (position) {
    case LEGEND_POSITION.RIGHT:
      return {
        top: defaultTopPadding,
        right: increasedRightPadding,
        bottom: defaultBottomPadding,
        left: defaultLeftPadding,
      }
    case LEGEND_POSITION.LEFT:
      return {
        top: defaultTopPadding,
        right: defaultRightPadding,
        bottom: defaultBottomPadding,
        left: increasedLeftPadding,
      }
    case LEGEND_POSITION.BOTTOM:
      return {
        top: defaultTopPadding,
        right: defaultRightPadding,
        bottom: increasedVerticalPadding,
        left: defaultLeftPadding,
      }
    case LEGEND_POSITION.TOP:
      return {
        top: increasedVerticalPadding,
        right: defaultRightPadding,
        bottom: defaultBottomPadding,
        left: defaultLeftPadding,
      }
    case LEGEND_POSITION.NONE:
      return {
        top: defaultTopPadding,
        right: defaultRightPadding,
        bottom: defaultBottomPadding,
        left: defaultLeftPadding,
      }
    default:
      return {
        top: defaultTopPadding,
        right: increasedRightPadding,
        bottom: defaultBottomPadding,
        left: defaultLeftPadding,
      }
  }
}

/**
 * Helper function to render the pie chart with correct color mapping
 */
const renderPieChart = (
  chartData: PieChartSlice[],
  statistic: STATISTIC_TYPE,
  legendPos: LEGEND_POSITION,
  noticeMsg: string | null,
  hideArcs: boolean
) => {
  // Format values based on statistic type
  const formatValue = (value: number | string): string => {
    if (value === undefined || value === null) return "N/A"

    const numValue = typeof value === "string" ? parseFloat(value) : value
    if (isNaN(numValue)) return String(value)

    // Handle percentage values correctly
    if (
      statistic === STATISTIC_TYPE.ROW_PERCENT ||
      statistic === STATISTIC_TYPE.COL_PERCENT ||
      statistic === STATISTIC_TYPE.PERC_PERCENT ||
      // Fallback for string-based checking if needed
      String(statistic).toLowerCase().includes("perc") ||
      String(statistic).toLowerCase().includes("percent") ||
      String(statistic).toLowerCase().includes("%")
    ) {
      // Nivo might already provide % values as 0-100, check if it's small
      return numValue <= 1 && numValue >= 0
        ? `${(numValue * 100).toFixed(1)}%` // Assume value is 0-1 range
        : `${numValue.toFixed(1)}%` // Assume value is already 0-100 range
    }

    // Format non-percentage numbers
    return numValue.toLocaleString() // Add commas for thousands
  }

  // Get legend configuration
  const legends = getLegendConfig(legendPos)

  // Get margins based on legend position
  const margins = getMargins(legendPos)

  // Transform data to be compatible with Nivo's interface
  const nivoCompatibleData = chartData.map((item) => ({
    id: item.id ?? "Unknown",
    label: item.label ?? item.id ?? "Unknown",
    value: item.value,
  }))

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {noticeMsg && (
        <div
          style={{
            position: "absolute",
            top: "5px",
            left: "0",
            right: "0",
            textAlign: "center",
            color: "#f59e0b", // Amber color for notice
            fontSize: "11px",
            zIndex: 10,
            padding: "0 10px", // Add padding so text doesn't hit edges
          }}
        >
          {noticeMsg}
        </div>
      )}
      <ResponsivePie
        data={nivoCompatibleData}
        margin={margins}
        innerRadius={0.5} // Doughnut hole size
        padAngle={0.7}
        cornerRadius={3}
        activeOuterRadiusOffset={8} // How much slices pop out on hover/click
        // Use the expanded color scheme to avoid color repetition
        colors={(datum) =>
          chartColorScheme[
            nivoCompatibleData.findIndex((item) => item.id === datum.id) %
              chartColorScheme.length
          ]
        }
        borderWidth={1}
        borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
        // Arc Labels (Labels on slices) - ALWAYS KEEP ENABLED
        enableArcLabels={true} // Always show labels on slices
        arcLabelsSkipAngle={0} // Show labels on ALL slices, even the smallest ones
        arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2.5]] }} // Darker text on slices
        // Arc Link Labels (Labels outside slices with lines)
        enableArcLinkLabels={!hideArcs} // Hide only the arms/labels outside if hideArcs is true
        arcLinkLabelsSkipAngle={7} // Reduced angle threshold for link labels
        arcLinkLabelsTextColor="#333333"
        arcLinkLabelsThickness={2}
        arcLinkLabelsColor={{ from: "color" }} // Line color matches slice
        arcLinkLabelsDiagonalLength={16} // Length of the first part of the line
        arcLinkLabelsStraightLength={24} // Length of the second part of the line
        arcLinkLabelsTextOffset={6} // Space between line end and text
        valueFormat={formatValue} // Use our custom formatter
        legends={legends} // Use legend config based on position
        // Make sure the tooltip always shows the formatted value
        tooltip={PieChartTooltip}
        theme={{
          labels: {
            text: {
              fontSize: 10,
              fontWeight: 600, // Make labels bolder to improve readability
            },
          },
          legends: {
            text: {
              fontSize: 11,
            },
          },
        }}
      />
    </div>
  )
}

/**
 * Improved PieChart component with expanded color scheme and forced labels on all slices
 */
const PieChart: React.FC<PieChartProps> = ({
  data,
  isChart = true,
  selectedStatistic = STATISTIC_TYPE.N,
  legendPosition = LEGEND_POSITION.RIGHT,
  hideArcLabels = false,
}) => {
  // Early validation
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <p>No data available for the chart</p>
      </div>
    )
  }

  if (!isChart) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <p>Unable to display this output as a chart. Please view as table.</p>
      </div>
    )
  }

  // Find the correct dataset for the selected statistic
  let statGroup = data.find(
    (d: PieChartStatisticGroup) => d.statistic === selectedStatistic
  )
  let notice: string | null = null

  // If we couldn't find the selected statistic, try the first available one
  if (
    !statGroup &&
    data.length > 0 &&
    data[0].data &&
    data[0].data.length > 0
  ) {
    console.warn(
      `Statistic '${selectedStatistic}' not found, using fallback '${data[0].statistic}'`
    )
    statGroup = data[0] // Use the first available group
    notice = `Note: Using ${statGroup.statistic} data (${selectedStatistic} not available)`
  }

  // Check if statGroup or its data is valid
  if (!statGroup || !statGroup.data || statGroup.data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <p>
          No data available for the selected statistic ({selectedStatistic}).
        </p>
      </div>
    )
  }

  // Filter out invalid items and 'Totals'
  const currentData = statGroup.data.filter(
    (item: PieChartSlice) =>
      item &&
      item.value !== null &&
      item.value !== undefined &&
      item.id !== null &&
      item.id !== undefined &&
      item.id !== "Totals" // Exclude Totals from the pie slices
  )

  if (currentData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <p>
          No valid data points found for {selectedStatistic} to display in the
          chart.
        </p>
      </div>
    )
  }

  return renderPieChart(
    currentData,
    statGroup.statistic, // Pass the actual statistic being used
    legendPosition,
    notice,
    hideArcLabels
  )
}

export default PieChart
